import { columnInfo, relevantColumnsForHaving } from "@vzbl/integrations";
import * as React from "react";
import { DateRangeSelect } from "src/components/DateRangeSelect";
import { FilterByIcon } from "src/components/FilterByIcon";
import { useIntegrationConfig } from "src/search/lib/useIntegrationConfig";
import { ColumnHaving, Comparator } from "src/search/types";
import { durationToRange } from "src/utils/date";
import { attributeToTitle } from "src/utils/strings";
import { Clause } from "./Clause";
import { HavingInputClause } from "./definitions";
import { NumbersInput } from "./NumbersInput";
import styles from "./question-input.module.css";
import { Space, Select, theme } from "antd";
import { usePopupContext } from "src/hooks/usePopup";
import { FieldTooltip } from "./ClauseTooltips";

export type HavingInputValue = {
  column?: string;
  operator?: string;
  values?: any[];
};

// Panel component that handles its own state independently
function HavingPanel({
  initialValue,
  value,
  onApply,
  onCancel,
}: {
  initialValue: HavingInputValue;
  value: any;
  onApply: (data: HavingInputValue) => void;
  onCancel: () => void;
}) {
  const { token } = theme.useToken();
  const integrationConfig = useIntegrationConfig();

  // Local state for the panel
  const [data, setData] = React.useState<HavingInputValue>(initialValue);

  // Reset data when panel opens with new initial value
  React.useEffect(() => {
    setData(initialValue);
  }, [initialValue]);

  // Get column metadata for the selected column
  const columnMeta = React.useMemo(
    () => columnInfo(data.column || "", integrationConfig.data as any),
    [data.column, integrationConfig.data]
  );

  // Get available columns
  const columns = React.useMemo(
    () => relevantColumnsForHaving(value, integrationConfig.data as any),
    [value, integrationConfig]
  );

  // Format column options
  const columnOptions = React.useMemo(
    () =>
      columns
        .sort((a, b) => a.key.toLowerCase().localeCompare(b.key.toLowerCase()))
        .map((column) => ({
          value: column.key,
          label: attributeToTitle(column.key),
        })),
    [columns]
  );

  // Enhanced search function for columns
  const searchColumn = React.useCallback((input: string, option: any) => {
    if (!input.trim()) return true;
    const searchableText = option.label.toLowerCase();
    const searchTerms = input.toLowerCase().split(/\s+/);
    return searchTerms.every((term) => searchableText.includes(term));
  }, []);

  // Get available operators based on column type
  const operators = React.useMemo(
    () => [
      {
        value: "WITHIN",
        label: "BETWEEN",
        description:
          columnMeta.type === "datetime"
            ? "Select a date range"
            : `Enter a range of numbers${
                columnMeta.unit ? ` (in ${columnMeta.unit})` : ""
              }`,
      },
    ],
    [columnMeta.type, columnMeta.unit]
  );

  // Render value selector based on column type and operator
  const renderValueSelector = () => {
    if (!data.column || !data.operator) return null;

    if (columnMeta.type === "number" && data.operator === "WITHIN") {
      return (
        <NumbersInput
          unit={columnMeta.unit}
          values={data.values || []}
          onChange={({ comparison }) => {
            const newValues = Object.values(comparison)[0];
            setData((current) => ({ ...current, values: newValues }));
          }}
          condition={
            {
              column: data.column,
              comparison: {
                [data.operator]: data.values,
              } as Comparator,
            } as ColumnHaving
          }
        />
      );
    }

    if (columnMeta.type === "datetime" && data.operator === "WITHIN") {
      return (
        <DateRangeSelect
          startDate={data.values?.[0]}
          endDate={data.values?.[1]}
          onChangeRange={(vals) =>
            setData((current) => ({ ...current, values: vals }))
          }
          onChangeDuration={(duration) => {
            const { startDate, endDate } = durationToRange(duration);
            setData((current) => ({
              ...current,
              values: [startDate, endDate],
            }));
          }}
        />
      );
    }

    return null;
  };

  return (
    <div className={styles.clausePanel} onClick={(e) => e.stopPropagation()}>
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Field Selection */}
        <div>
          <FieldTooltip type="havingField">
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: token.colorTextSecondary,
                marginBottom: 8,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Select a Measure
            </div>
          </FieldTooltip>
          <Select
            style={{ width: "100%" }}
            showSearch
            placeholder="Select a measure (e.g., Avg Pull Request Time)"
            value={data.column}
            onChange={(col) =>
              setData((current) => ({
                ...current,
                column: col,
                operator: undefined,
                values: undefined,
              }))
            }
            options={columnOptions}
            filterOption={searchColumn}
            optionFilterProp="label"
          />
        </div>

        {/* Operator Selection */}
        <div>
          <FieldTooltip type="havingOperator">
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: token.colorTextSecondary,
                marginBottom: 8,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Operator
            </div>
          </FieldTooltip>
          <Select
            style={{ width: "100%" }}
            placeholder={
              data.column
                ? "Choose a condition (e.g., Greater than, Less than)"
                : "Select a measure first"
            }
            value={data.operator}
            onChange={(op) =>
              setData((current) => ({
                ...current,
                operator: op,
                values: undefined,
              }))
            }
            options={operators}
            disabled={!data.column}
          />
        </div>

        {/* Value Selection */}
        <div>
          <FieldTooltip type="havingValue">
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: token.colorTextSecondary,
                marginBottom: 8,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Threshold
            </div>
          </FieldTooltip>
          {renderValueSelector()}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            paddingTop: 16,
            marginTop: 8,
          }}
        >
          <div
            style={{
              fontSize: "0.85em",
              color: token.colorTextDescription,
            }}
          >
            {(() => {
              if (!data.column) return "Select a measure to begin";
              if (!data.operator)
                return "Choose the condition (Greater than, Less than)";
              if (!data.values?.length)
                return "Enter a target value (e.g., 5 days)";
              return "Ready to apply filter";
            })()}
          </div>
          <Space>
            <button
              onClick={onCancel}
              style={{
                background: "none",
                border: "none",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                color: token.colorTextSecondary,
                fontSize: 14,
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = token.colorBgTextHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Cancel
            </button>
            <button
              style={{
                fontSize: 14,
                backgroundColor: token.colorPrimary,
                color: token.colorTextLightSolid,
                border: "none",
                padding: "5px 16px",
                borderRadius: 6,
                cursor:
                  data.column && data.operator && data.values?.length
                    ? "pointer"
                    : "not-allowed",
                opacity:
                  data.column && data.operator && data.values?.length ? 1 : 0.5,
                transition: "all 0.2s",
                boxShadow: token.boxShadowSecondary,
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor =
                    token.colorPrimaryHover;
                }
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = token.colorPrimary;
                }
              }}
              disabled={!data.column || !data.operator || !data.values?.length}
              onClick={() => {
                if (data.column && data.operator && data.values?.length) {
                  onApply(data);
                }
              }}
            >
              Apply
            </button>
          </Space>
        </div>
      </Space>
    </div>
  );
}

// Pill component that displays the current value
function HavingPill({ data }: { data: HavingInputValue }) {
  const { token } = theme.useToken();
  return (
    <span>
      {data.column && (
        <>
          <span>{data.column}</span>
          {data.operator && (
            <>
              <span style={{ opacity: token.opacityLoading }}> </span>
              <span>{data.operator} </span>
            </>
          )}
          {data.values?.length && <span>{data.values.join(" - ")}</span>}
        </>
      )}
    </span>
  );
}

export function HavingClause({
  clause,
  value,
  updateClause,
  removeClause,
  sameAsLast,
}: {
  clause: HavingInputClause;
  value: any;
  updateClause: (clause: any) => void;
  removeClause: (clause: any) => void;
  sameAsLast?: boolean;
}) {
  const popup = usePopupContext();
  const data = React.useMemo(
    () => conditionToInputType(clause.value),
    [clause.value]
  );

  const handleApply = (newData: HavingInputValue) => {
    if (newData.column && newData.operator && newData.values?.length) {
      updateClause({
        ...clause,
        value: {
          column: newData.column,
          comparison: {
            [newData.operator]: newData.values,
          } as Comparator,
        } as ColumnHaving,
      });
    }
    popup.close();
  };

  return (
    <Clause
      onRemove={() => removeClause(clause)}
      onMenuClose={() => popup.close()}
      renderMenu={() => (
        <HavingPanel
          initialValue={data}
          onApply={handleApply}
          onCancel={() => popup.close()}
          value={value}
        />
      )}
      clause={clause}
      title={sameAsLast ? "" : "Having"}
      icon={
        sameAsLast ? undefined : (
          <FilterByIcon size={10} style={{ opacity: 0.7 }} />
        )
      }
    >
      <HavingPill data={data} />
    </Clause>
  );
}

// Helper function to convert a condition to input type
export const conditionToInputType = (
  value: ColumnHaving | undefined
): HavingInputValue => {
  // WITHIN is the only available option at this time
  return {
    column: value?.column,
    operator: "WITHIN",
    values:
      value?.comparison[
        (Object.keys(value?.comparison || {}).at(0) || "") as keyof Comparator
      ],
  };
};
